import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Box, Button, FormControl, FormHelperText, Grid, MenuItem, Select, TextField, Typography } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import LockIcon from '@mui/icons-material/Lock';

// import { useStyles } from '../InsuranceServiceForm/style';
import { useStyles } from 'features/salesOrder/components/SalesOrderServiceForm/components/InsuranceServiceForm/style';
import { checkShowErrorMessage } from 'features/common/helpers';
import {
	ICodeName,
	IOtherService,
	IServiceCostVariationLogs,
	ServiceUnitEnum,
	TypeServiceEnum,
	typeUnit,
} from 'features/salesOrder/types';
import { ControlledSelect } from 'features/common/components/ControlledSelect';
import { ControlledAutoComplete } from 'features/common/components/ControlledAutoComplete';
import { ControlledTextField } from 'features/common/components/ControlledTextField';
import { ControlledCheckBox } from 'features/common/components/ControlledCheckBox';
import { ControlledDatePicker } from 'features/common/components/ControlledDatePicker';
import { StayDurationHint } from 'features/common/components/StayDurationHint';
import {
	calcularPrecioFinal,
	calculatMarkupPercentaje,
	excludeTypeUnits,
	filterServiceCostVariationLogs,
} from 'features/salesOrder/components/SalesOrderServiceForm/helpers';
import { AutocompleteProviders } from 'features/common/components/AutocompleteProviders';
import { ServiceCostVariationLogs } from 'features/salesOrder/components/SalesOrderServiceForm/components/ServiceCostVariationLogs';
import { getCostVariationLogsByService, sendOtherServiceIssueRequest } from 'features/salesOrder/services';
import { useSaleOrderId } from '../../../context/SaleOrderScopeContext';
import { useServiceQuickEdit } from '../../../context/ServiceQuickEditContext';
import { executeServicePut } from '../../../helpers/executeServicePut';
import { ShowAlertState } from 'features/common/types';
import { useServiceMarketCountry } from '../../../hooks/useServiceMarketCountry';
import { getRates } from 'features/stats/services';
import { extractErrorMessage } from '../../../../../../features/quotation/helpers';
import IssueRequestButton from '../../IssueRequestButton';
import IssueDetailButton from '../../IssueDetailButton';
import ServiceBillingStatusDetail from '../ServiceBillingStatusDetail';
import ServicePurchaseOrderDetail from '../ServicePurchaseOrderDetail';
import { ProviderPaymentDateSection } from '../ProviderPaymentDateSection';
import { useServiceProviderPaymentConstraints } from '../../../hooks/useServiceProviderPaymentConstraints';
import ProviderPaymentConstraintsAlert from '../ProviderPaymentConstraintsAlert';
import ReferenceValidationAlert from '../ReferenceValidationAlert';
import { SERVICE_FORM_INITIAL_FOCUS_ID } from '../../../helpers';
import {
	buildMinTotalCostRules,
	getTariffProviderIdFromFormData,
	getTariffProviderIdFromService,
	validateProviderPaymentConstraintsBeforeSave,
} from '../../../helpers/providerPaymentConstraints';

const format = (date: string) => {
	// const arrayDate = date.split('-');
	// return `${arrayDate[2]}-${arrayDate[1]}-${arrayDate[0]}`;
	return date;
};

interface Props {
	index: number;
	passengers: { keyId: string; name: string; lastName: string }[];
	service: IOtherService;
	onUpdateService: () => void;
	currencyesList: ICodeName[];
	onSetAlert: (values: ShowAlertState) => void;
	onSetLoading: (value: boolean) => void;
	isExistingService?: boolean;
}
const iconMapping = {
	Persona: <PersonIcon />,
	Grupo: <GroupIcon />,
};

const issueRequestHelpText =
	'Para poder solicitar la emisión, el servicio debe haberse guardado previamente y la referencia debe de ser: -';

const otherServiceTypeOptionsAllowed = [
	{ name: 'Asistencia en viajes o Seguros', value: 'seguros' },
	{ name: 'Actividades, paseos o excursiones de solo 1 día', value: 'actividades_menos1dia' },
	{ name: 'Paquetes o excursiones de más de 1 día', value: 'pkts_o_exc_mas1dia' },
	{ name: 'Ferry (ej. Buquebus, Baleares, etc)', value: 'ferry' },
	{ name: 'Alquiler de auto', value: 'rent_a_car' },
	{ name: 'Crucero', value: 'crucero' },
	{ name: 'Circuito', value: 'circuito' },
	{ name: 'Eventos o Conciertos con entradas', value: 'evento_o_concierto' },
	{ name: 'Entradas a espectáculos o parques', value: 'entradas' },
	{ name: 'Visas u otros Documentos', value: 'documentos' },
	{ name: 'Merchandising mevuelo.com', value: 'merchandising' },
	{ name: 'Devoluciones', value: 'devolucion' },
	{ name: 'ADMs o Penalidades', value: 'adm_o_penalizacion' },
	{ name: 'Otros (detallar en Nombre y Notas internas)', value: 'otros_servicios' },
];

const otherServiceTypeOptionsNotAllowed = [
	{ name: 'Ancillaries de vuelos', value: 'flight_ancillaries' },
	{ name: 'Fee', value: 'fee' },
	{ name: 'Optimización', value: 'opt' },
	{ name: 'Costo de medio de pago', value: 'medio_de_pago' },
	{ name: 'Costo de transferencia', value: 'transferencia' },
];

const getOtherServiceTypeOptions = (service: IOtherService, isExistingService: boolean | undefined) => {
	if (!isExistingService) {
		return otherServiceTypeOptionsAllowed;
	} else {
		const maybeOptionInNotAllowed = otherServiceTypeOptionsNotAllowed.find(
			(option) => option.value === service.otherServiceType,
		);
		return maybeOptionInNotAllowed
			? otherServiceTypeOptionsAllowed.concat([maybeOptionInNotAllowed])
			: otherServiceTypeOptionsAllowed;
	}
};

export const OtherService = ({
	index,
	passengers,
	service,
	currencyesList,
	onUpdateService,
	onSetAlert,
	onSetLoading,
	isExistingService,
}: Props): JSX.Element => {
	const classes = useStyles();
	const methods = useForm();
	const {
		control,
		formState: { errors },
		setValue,
		getValues,
		resetField,
		watch,
	} = methods;

	const idService = service.keyId;

	const [tariffProviderId, setTariffProviderId] = useState('n/a');
	const saleOrderId = useSaleOrderId();
	const quickEdit = useServiceQuickEdit();
	const [serviceCostVariationLogs, setServiceCostVariationLogs] = useState<IServiceCostVariationLogs[]>([]);

	const referenceValue = watch('reference' as `${string}.${string | number}`) as string;
	const tariffProviderIdWatched =
		(watch('tariffProviderId' as `${string}.${string | number}`) as string) || tariffProviderId;
	const filteredArray = filterServiceCostVariationLogs(serviceCostVariationLogs, idService);

	const theCountry = useServiceMarketCountry();
	const paymentConstraints = useServiceProviderPaymentConstraints(service.keyId);
	const initialTariffProviderId = useMemo(() => getTariffProviderIdFromService(service), [service]);

	const loadCostLog = useCallback(async () => {
		const result = await getCostVariationLogsByService(saleOrderId, idService, quickEdit?.agencyId);
		setServiceCostVariationLogs(result.data);
	}, [setServiceCostVariationLogs]);

	useEffect(() => {
		loadCostLog();
	}, [loadCostLog, service]);

	const handleCostChange = (newCost: number) => {
		// @ts-ignore
		setValue('cost', newCost, { shouldDirty: true });
		const saleAmount = getValues('saleAmount' as `${string}.${string | number}`) || 0;
		const newMarkup = calculatMarkupPercentaje(newCost, saleAmount) as string;
		// @ts-ignore
		setValue('markupPercentage', newMarkup, { shouldDirty: true });
		calculateTotalCost();
	};

	const handleSaleAmountChange = (newValue: number) => {
		if (theCountry === 'CO') {
			const rateValue = getValues('currencyRate' as `${string}.${string | number}`) || 1;
			const valorLocalCurrency = newValue * rateValue;
			setValue('saleAmountInLocalCurrency', valorLocalCurrency, { shouldDirty: true });
		}
		// @ts-ignore
		setValue('saleAmount', newValue, { shouldDirty: true });
		const cost = getValues('cost' as `${string}.${string | number}`) || 0;
		if (newValue === 0) {
			setValue('markupPercentage', 0);
		} else {
			const newMarkup = calculatMarkupPercentaje(cost, newValue) as string;

			// @ts-ignore
			setValue('markupPercentage', newMarkup, { shouldDirty: true });
		}

		calculateTotalCost();
	};

	const handleSaleAmountChangeInLocalCurrency = (valorEnCop: number) => {
		setValue('saleAmountInLocalCurrency', valorEnCop, { shouldDirty: true });
		const rateValue = getValues('currencyRate' as `${string}.${string | number}`) || 1;
		const valorEnDolares = valorEnCop / rateValue;
		// @ts-ignore
		setValue('saleAmount', valorEnDolares, { shouldDirty: true });
		const cost = getValues('cost' as `${string}.${string | number}`) || 0;
		if (valorEnDolares === 0) {
			setValue('markupPercentage', 0);
		} else {
			const newMarkup = calculatMarkupPercentaje(cost, valorEnDolares) as string;

			// @ts-ignore
			setValue('markupPercentage', newMarkup, { shouldDirty: true });
		}

		calculateTotalCost();
	};

	const handleMarkupPercentageChange = (newValue: number) => {
		// @ts-ignore
		setValue('markupPercentage', newValue, { shouldDirty: true });
		const cost = getValues('cost' as `${string}.${string | number}`) || 0;
		const newSaleAmount = calcularPrecioFinal(cost, newValue) as string;

		if (theCountry === 'CO') {
			const rateValue = getValues('currencyRate' as `${string}.${string | number}`) || 1;
			const valorLocalCurrency = Number(newSaleAmount) * rateValue;
			setValue('saleAmountInLocalCurrency', valorLocalCurrency, { shouldDirty: true });
		}
		// @ts-ignore
		setValue('saleAmount', newSaleAmount, { shouldDirty: true });
		calculateTotalCost();
	};

	const sameTariffServiceProvider = watch('sameTariffServiceProvider' as `${string}.${string | number}`, false);

	const passengersSelected = watch('passengerIds' as `${string}.${string | number}`) || [];
	const unit = watch('unit' as `${string}.${string | number}`);

	const calculateTotalCost = useCallback(() => {
		let cost = getValues('cost' as `${string}.${string | number}`) || 0;
		let saleAmount = getValues('saleAmount' as `${string}.${string | number}`) || 0;
		let saleAmountInLocalCurrency = getValues('saleAmountInLocalCurrency' as `${string}.${string | number}`) || 0;

		if (unit === 'Person') {
			cost = cost * passengersSelected.length;
			saleAmount = saleAmount * passengersSelected.length;
			saleAmountInLocalCurrency = saleAmountInLocalCurrency * passengersSelected.length;
		}
		setValue('totalCost' as `${string}.${string | number}`, cost);
		setValue('totalSaleAmount' as `${string}.${string | number}`, saleAmount);
		if (theCountry === 'CO') {
			const totalEnColombianos = saleAmountInLocalCurrency;
			setValue('totalSaleAmountInLocalCurrency', totalEnColombianos);
		}
	}, [getValues, passengersSelected, setValue, unit, theCountry]);

	const handleUpdateService = async (data: IOtherService) => {
		try {
			onSetLoading(true);

			if (
				!validateProviderPaymentConstraintsBeforeSave({
					constraints: paymentConstraints,
					totalCost: Number(data.totalCost),
					initialTariffProviderId,
					submittedTariffProviderId: getTariffProviderIdFromFormData(data),
					onSetAlert,
					onSetLoading,
				})
			) {
				return;
			}

			data.startDate = format(data.startDate);
			data.endDate = format(data.endDate);
			data.limitDate = format(data.limitDate);

			// @ts-ignore
			if (data.tariffProviderId === 'n/a') {
				onSetAlert({
					show: true,
					severity: 'warning',
					message: 'Elije un proveedor de Tarifa',
				});
				// @ts-ignore
				delete data.tariffProviderId;
			}

			// @ts-ignore
			if (data.serviceProviderId === 'n/a' && !data.sameTariffServiceProvider) {
				onSetAlert({
					show: true,
					severity: 'warning',
					message: 'Elije un proveedor de Servicio',
				});
			}
			// @ts-ignore
			if (data.serviceProviderId === 'n/a' && data.sameTariffServiceProvider) {
				// @ts-ignore
				data.serviceProviderId = data.tariffProviderId;
			}

			if (data.totalSaleAmount === null) {
				// @ts-ignore
				data.totalSaleAmount = 0;
			}

			if (theCountry !== 'CO') {
				// @ts-ignore
				delete data.currencyRate;
				// @ts-ignore
				delete data.totalSaleAmountInLocalCurrency;
			}

			data.serviceType = TypeServiceEnum.Other;

			const saved = await executeServicePut({
				saleOrderId,
				serviceId: data.keyId,
				data,
				quickEdit,
			});
			if (!saved) {
				onSetLoading(false);
				return;
			}

			if (!quickEdit) {
				onSetAlert({
					show: true,
					severity: 'success',
					message: 'Otros guardado con exito!',
				});
				onUpdateService();
			}
		} catch (e) {
			// @ts-ignore
			alert(e.response.data.message);
		} finally {
			onSetLoading(false);
		}
	};

	useEffect(() => {
		if (sameTariffServiceProvider) {
			setValue('serviceProviderId' as `${string}.${string | number}`, tariffProviderId);
		}
		if (!sameTariffServiceProvider) {
			setValue('sameTariffServiceProvider' as `${string}.${string | number}`, false);
		}
	}, [sameTariffServiceProvider, tariffProviderId]);

	useEffect(() => {
		const limitDateField = document.querySelector('input[name="limitDate"]');
		const startDate = document.querySelector('input[name="startDate"]');
		const endDate = document.querySelector('input[name="endDate"]');
		if (limitDateField) {
			// @ts-ignore
			limitDateField.value = '';
		}
		if (startDate) {
			// @ts-ignore
			startDate.value = '';
		}
		if (endDate) {
			// @ts-ignore
			endDate.value = '';
		}

		const effectTariffProviderId =
			typeof service.tariffProvider === 'string'
				? service.tariffProvider || 'n/a'
				: service.tariffProvider?.id || 'n/a';
		setTariffProviderId(effectTariffProviderId);
		const effectServiceProviderId = service.serviceProvider?.id || 'n/a';
		setValue('reference' as `${string}.${string | number}`, service.reference || '');
		setValue('tariffProviderId' as `${string}.${string | number}`, effectTariffProviderId);
		setValue('serviceProviderId' as `${string}.${string | number}`, effectServiceProviderId);
		setValue(
			'unit' as `${string}.${string | number}`,
			service.unit.length === 0 ? ServiceUnitEnum.Person : service.unit,
			{ shouldDirty: true },
		);
		setValue('cost' as `${string}.${string | number}`, service.cost || 0);
		setValue('keyId' as `${string}.${string | number}`, service.keyId);
		setValue('saleAmount' as `${string}.${string | number}`, service.saleAmount || 0);
		setValue('currency' as `${string}.${string | number}`, service.currency || 'USD');
		setValue('markupPercentage' as `${string}.${string | number}`, service.markupPercentage || 0);
		setValue('voucherNotes' as `${string}.${string | number}`, service.voucherNotes || '');
		setValue('internalNotes' as `${string}.${string | number}`, service.internalNotes || '');
		setValue('passengerIds' as `${string}.${string | number}`, service.passengerIds || '');

		setValue('sameTariffServiceProvider' as `${string}.${string | number}`, service.sameTariffServiceProvider || false);
		setValue('otherServiceType' as `${string}.${string | number}`, service.otherServiceType || '');
		setValue('serviceName' as `${string}.${string | number}`, service.serviceName || '');
		setValue('startDate' as `${string}.${string | number}`, service.startDate || '');
		setValue('endDate' as `${string}.${string | number}`, service.endDate || '');
		setValue('limitDate' as `${string}.${string | number}`, service.limitDate || '');

		service.cost && handleCostChange(Number(service.cost || 0));
		service.saleAmount && handleSaleAmountChange(Number(service.saleAmount || 0));

		if (theCountry === 'CO') {
			if (!isExistingService) {
				const fetchRates = async () => {
					const result = await getRates();
					setValue('currencyRate', result.data.ratesList[0].values[0].effectiveRate);
				};
				fetchRates();
			} else {
				setValue('currencyRate', service.currencyRate || 1);
				setValue('saleAmountInLocalCurrency' as `${string}.${string | number}`, service.saleAmountInLocalCurrency || 1);
			}
			setValue('totalSaleAmountInLocalCurrency', service.totalSaleAmountInLocalCurrency || 1);
		}

		// handleCostChange(Number(service.cost));
		// handleSaleAmountChange(Number(service.saleAmount));
	}, [setValue, index, service, isExistingService, theCountry]);

	const paymentDateHydrateKey = useMemo(() => {
		const lines = service?.providerManualPaymentDateLines ?? [];
		const linesKey = lines
			.map((l) => `${l.id ?? 'n'}|${l.paymentDate ?? ''}|${l.amount ?? ''}|${l.observation ?? ''}`)
			.join('~');

		return `${service?.keyId ?? 'no'}|${service?.automaticProviderPaymentDateCalculation === true}|${
			lines.length
		}|${linesKey}`;
	}, [service?.keyId, service?.automaticProviderPaymentDateCalculation, service?.providerManualPaymentDateLines]);

	useEffect(() => {
		if (!service?.keyId) return;

		setValue('automaticProviderPaymentDateCalculation', service.automaticProviderPaymentDateCalculation === true, {
			shouldDirty: false,
			shouldTouch: false,
			shouldValidate: false,
		});

		const normalized = (service.providerManualPaymentDateLines ?? []).map((l) => ({
			id: l.id ?? null,
			paymentDate: l.paymentDate ?? '',
			amount: l.amount ?? null,
			observation: l.observation ?? '',
		}));

		setValue('providerManualPaymentDateLines', normalized, {
			shouldDirty: false,
			shouldTouch: false,
			shouldValidate: false,
		});
	}, [paymentDateHydrateKey, setValue]);

	useEffect(() => {
		setValue('currency' as `${string}.${string | number}`, 'USD');
	}, [setValue]);

	const [selectedValue, setSelectedValue] = useState('');

	useEffect(() => {
		// Suponiendo que `service.otherServiceType` es el valor que viene de la API
		if (service && service.otherServiceType) {
			setSelectedValue(service.otherServiceType);
		}
	}, [service]);

	const handleChange = (event: { target: { value: React.SetStateAction<string> } }) => {
		setSelectedValue(event.target.value);
	};

	calculateTotalCost();

	const isButtonIssueRequestEnabled = isExistingService && service.reference == '-';

	const handleSendIssueRequest = async () => {
		try {
			onSetLoading(true);
			await sendOtherServiceIssueRequest(saleOrderId, idService);
			onSetAlert({
				show: true,
				severity: 'success',
				message: 'Solicitud de emisión enviada satisfactoriamente',
			});
		} catch (error) {
			onSetAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Error enviando la solicitud de emisión'),
			});
		} finally {
			setTimeout(() => {
				onSetLoading(false);
				onUpdateService();
			}, 500);
		}
	};

	const typeOptions = getOtherServiceTypeOptions(service, isExistingService);

	return (
		<FormProvider {...methods}>
			<form
				onSubmit={methods.handleSubmit((data) => {
					handleUpdateService(data as IOtherService);
				})}
			>
				<div>
					<Typography variant="h6">Descripción</Typography>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={12}>
							<Typography color={'Grey'} mb={1}>
								Pasajeros
							</Typography>
							<ControlledAutoComplete
								name={'passengerIds'}
								rules={{
									required: { value: true, message: 'Este campo es requerido' },
								}}
								options={passengers}
								isMultiple={true}
								helperText={checkShowErrorMessage(Boolean(errors.passengerIds), errors.passengerIds?.message)}
							/>
						</Grid>
						<Grid item xs={12} sm={12}>
							<Typography color={'Grey'}>Referencia</Typography>
							<ControlledTextField
								name={'reference'}
								inputProps={!isExistingService ? { id: SERVICE_FORM_INITIAL_FOCUS_ID } : undefined}
								rules={{
									required: { value: true, message: 'Este campo es requerido' },
								}}
								helperText={checkShowErrorMessage(Boolean(errors.reference), errors.reference?.message)}
							/>
							{tariffProviderIdWatched && tariffProviderIdWatched !== 'n/a' && (
								<ReferenceValidationAlert providerId={tariffProviderIdWatched} reference={referenceValue ?? ''} />
							)}
						</Grid>
						<Grid container spacing={2} className={classes.containerSelect}>
							<Grid item xs={12}>
								<ProviderPaymentConstraintsAlert constraints={paymentConstraints} />
							</Grid>
							<Grid item sm={4}>
								<Typography color={'Grey'}>Proveedor de tarifas</Typography>
								<AutocompleteProviders
									name={'tariffProviderId'}
									disabled={paymentConstraints.lockTariffProvider}
									rules={{
										required: { value: true, message: 'Este campo es requerido' },
									}}
								/>
							</Grid>
							<Grid item sm={4} display="flex" justifyContent="center" alignItems="center">
								<ControlledCheckBox name={'sameTariffServiceProvider'} label="Mismo proveedor de tarifa/servicio" />
							</Grid>

							{!sameTariffServiceProvider && (
								<Grid item sm={4}>
									<Typography color={'Grey'}>Prestador de servicio</Typography>
									<AutocompleteProviders
										name={'serviceProviderId'}
										rules={{
											required: { value: true, message: 'Este campo es requerido' },
										}}
									/>
								</Grid>
							)}
						</Grid>
						<Grid item xs={12} sm={6}>
							<Typography color={'Grey'}>Tipo</Typography>
							<FormControl size="small" fullWidth error={Boolean(errors.otherServiceType)}>
								<Controller
									name="otherServiceType"
									control={control}
									render={({ field }) => (
										<Select
											{...field}
											value={selectedValue}
											onChange={(e) => {
												handleChange(e);
												field.onChange(e);
											}}
										>
											{typeOptions.map((option) => (
												<MenuItem key={option.value} value={option.value}>
													{option.name}
												</MenuItem>
											))}
										</Select>
									)}
								/>
								{errors.otherServiceType && <FormHelperText>{errors.otherServiceType.message}</FormHelperText>}
							</FormControl>
							{/* <Typography color={'Grey'}>Tipo</Typography>
							<ControlledSelect
								name={'otherServiceType'}
								rules={{
									required: { value: true, message: 'Este campo es requerido' },
								}}
								options={typeOptions}
							/> */}
						</Grid>
						<Grid item xs={12} sm={6}>
							<Typography color={'Grey'}>Nombre del servicio</Typography>
							<ControlledTextField
								name={'serviceName'}
								rules={{
									required: { value: true, message: 'Este campo es requerido' },
								}}
								helperText={checkShowErrorMessage(Boolean(errors.serviceName), errors.serviceName?.message)}
							/>
						</Grid>
						<Grid item xs={12} sm={4}>
							<Typography color={'Grey'}>Fecha inicial</Typography>
							<ControlledDatePicker
								name={'startDate'}
								rules={{ required: 'Este campo es requerido' }}
								required={true}
							/>
						</Grid>
						<Grid item xs={12} sm={4}>
							<Typography color={'Grey'}>Fecha final</Typography>
							<ControlledDatePicker name={'endDate'} rules={{ required: 'Este campo es requerido' }} required={true} />
						</Grid>
						<Grid item xs={12} sm={4}>
							<Typography color={'Grey'}>Fecha limite</Typography>
							<ControlledDatePicker
								name={'limitDate'}
								rules={{ required: 'Este campo es requerido' }}
								inputFormat="dd MMM, yyyy"
								required={true}
							/>
						</Grid>
						<Grid item xs={12}>
							<StayDurationHint startDateName="startDate" endDateName="endDate" variant="accommodation" />
						</Grid>
						<Grid item xs={12} sm={2}>
							<Typography color={'Grey'}>Unidad por</Typography>
							<ControlledSelect
								name={'unit'}
								rules={{
									required: { value: true, message: 'Este campo es requerido' },
								}}
								options={excludeTypeUnits(typeUnit, ['Habitacion', 'Vehiculo', 'Camarote'])}
								iconTypeTrip={iconMapping}
							/>
						</Grid>
						<Grid item xs={12} sm={2}>
							<Typography color={'Grey'}>Moneda</Typography>
							<ControlledSelect
								name={'currency'}
								rules={{
									required: { value: true, message: 'Este campo es requerido' },
								}}
								options={currencyesList.map((item) => ({ id: item.code.toString(), name: item.code }))}
							/>
						</Grid>
						<Grid item xs={12} sm={3}>
							<Typography color={'Grey'}>Costo</Typography>
							<ControlledTextField
								name={'cost'}
								isNumber
								rules={buildMinTotalCostRules(
									paymentConstraints.minAllowedCost,
									paymentConstraints.hasProviderPayments,
									{
										required: { value: true, message: 'Este campo es requerido' },
									},
								)}
								onChange={(value) => handleCostChange(parseFloat(value))}
								helperText={checkShowErrorMessage(Boolean(errors.cost), errors.cost?.message)}
							/>
						</Grid>
						<Grid item xs={12} sm={2}>
							<Typography color={'Grey'}>Markup %</Typography>
							<ControlledTextField
								name={'markupPercentage'}
								rules={{
									required: { value: true, message: 'Este campo es requerido' },
								}}
								isNumber
								onChange={(value) => handleMarkupPercentageChange(parseFloat(value))}
								helperText={checkShowErrorMessage(Boolean(errors.markupPercentage), errors.markupPercentage?.message)}
							/>
						</Grid>
						<Grid item xs={12} sm={3}>
							<Typography color={'Grey'}>Venta</Typography>
							<ControlledTextField
								isNumber
								name={'saleAmount'}
								rules={{
									required: { value: true, message: 'Este campo es requerido' },
								}}
								onChange={(value) => handleSaleAmountChange(parseFloat(value))}
								helperText={checkShowErrorMessage(Boolean(errors.saleAmount), errors.saleAmount?.message)}
							/>
						</Grid>
						{theCountry === 'CO' && (
							<>
								<Grid item xs={12} sm={3} />
								<Grid item xs={12} sm={3} />
								<Grid item xs={12} sm={3} />
								<Grid item xs={12} sm={3}>
									<Typography color={'Grey'}>Venta COP</Typography>
									<ControlledTextField
										name={'saleAmountInLocalCurrency'}
										onChange={(value) => handleSaleAmountChangeInLocalCurrency(parseFloat(value))}
										helperText={checkShowErrorMessage(Boolean(errors.saleAmount), errors.saleAmount?.message)}
									/>
								</Grid>
							</>
						)}
						<Grid item xs={12} sm={4} />
						<Grid item xs={12} sm={4}>
							<Typography color={'Grey'}>Costo Total</Typography>
							<ControlledTextField name={'totalCost'} disabled />
						</Grid>
						<Grid item xs={12} sm={4}>
							<Typography color={'Grey'}>Venta Total</Typography>
							<ControlledTextField name={'totalSaleAmount'} disabled />
						</Grid>
						{theCountry === 'CO' && (
							<>
								<Grid item xs={12} sm={4} />
								<Grid item xs={12} sm={4}>
									<Typography color={'Grey'}>Tipo de Cambio</Typography>
									<ControlledTextField
										name={'currencyRate'}
										disabled={isExistingService}
										onChange={calculateTotalCost}
									/>
								</Grid>
								<Grid item xs={12} sm={4}>
									<Typography color={'Grey'}>Total en COP</Typography>
									<ControlledTextField name={'totalSaleAmountInLocalCurrency'} disabled />
								</Grid>
							</>
						)}
						<Grid item xs={12} sm={12}>
							<Typography color={'Grey'}>Notas de voucher</Typography>
							<Controller
								// @ts-ignore
								name={'voucherNotes'}
								control={control}
								defaultValue="Favor de presentar voucher en el check-in"
								render={({ field }) => (
									<TextField
										fullWidth
										className={classes.textField}
										variant="outlined"
										multiline
										rows={5}
										{...field}
										helperText={checkShowErrorMessage(Boolean(errors.voucherNotes), errors.voucherNotes?.message)}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={12}>
							<Typography color={'Grey'}>
								<LockIcon /> Notas internas
							</Typography>
							<Controller
								// @ts-ignore
								name={'internalNotes'}
								control={control}
								render={({ field }) => (
									<TextField
										fullWidth
										className={classes.textField}
										variant="outlined"
										multiline
										rows={5}
										{...field}
										helperText={checkShowErrorMessage(Boolean(errors.voucherNotes), errors.voucherNotes?.message)}
									/>
								)}
							/>
						</Grid>
						<ServiceCostVariationLogs filteredArray={filteredArray} />
					</Grid>
				</div>

				<ServiceBillingStatusDetail billingStatusDetail={service.billingStatusDetail} />
				<ServicePurchaseOrderDetail serviceId={service.keyId} serviceType={service.serviceType} />
				<ProviderPaymentDateSection
					hydrateKey={paymentDateHydrateKey}
					control={control}
					watch={watch}
					setValue={setValue}
					getValues={getValues}
					clearErrors={methods.clearErrors}
					resetField={resetField}
					errors={errors}
					servicePath={''}
					providerIdPath={'tariffProviderId'}
					providerAutomaticPaymentDateLine={service.providerAutomaticPaymentDateLine}
				/>
				{(service.enable || service.enable === undefined) && (
					<Box display="flex" flexDirection="row-reverse" mt={2} gap={2}>
						<IssueRequestButton
							showButton={!service.issueRequest}
							disabled={!isButtonIssueRequestEnabled}
							handleClick={handleSendIssueRequest}
							helpText={issueRequestHelpText}
						/>
						<IssueDetailButton issueRequestDetail={service.issueRequest} />
						<Button type="submit" variant="contained" color="success">
							Guardar
						</Button>
					</Box>
				)}
			</form>
		</FormProvider>
	);
};
